import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface ChatMessage {
  id: string;
  type: "received" | "sent";
  content: string;
  time: string;
  isPreview?: boolean; // 미리보기 상태 (아직 전송 안됨)
}

interface KakaoMockChatProps {
  customerName?: string;
  initialMessages?: ChatMessage[];
  previewMessage?: string | null; // 정산서 미리보기 메시지
  onPaste?: () => void; // 붙여넣기 버튼 클릭 시
  isPasted?: boolean; // 붙여넣기 완료 여부
  className?: string;
  showPreview?: boolean; // 미리보기 말풍선 표시 여부
}

export function KakaoMockChat({
  customerName = "손님",
  initialMessages = [],
  previewMessage,
  onPaste,
  isPasted = false,
  className,
  showPreview = true,
}: KakaoMockChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [showPasteHint, setShowPasteHint] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // 붙여넣기 완료 시 메시지 추가
  useEffect(() => {
    if (isPasted && previewMessage) {
      const now = new Date();
      const timeStr = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;

      setMessages((prev) => [
        ...prev.filter((m) => !m.isPreview), // 미리보기 메시지 제거
        {
          id: `sent-${Date.now()}`,
          type: "sent",
          content: previewMessage,
          time: timeStr,
          isPreview: false,
        },
      ]);
    }
  }, [isPasted, previewMessage]);

  // 메시지 추가 시 스크롤
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, previewMessage]);

  // 붙여넣기 힌트 표시 (미리보기가 있고, 아직 붙여넣기 안했을 때)
  useEffect(() => {
    if (previewMessage && !isPasted) {
      setShowPasteHint(true);
    } else {
      setShowPasteHint(false);
    }
  }, [previewMessage, isPasted]);

  return (
    <div className={cn("flex flex-col h-full bg-[#B2C7D9] rounded-xl overflow-hidden shadow-lg", className)}>
      {/* 헤더 */}
      <div className="flex items-center gap-3 px-4 py-3 bg-[#B2C7D9]/90 border-b border-[#9BB3C9] backdrop-blur-sm z-10">
        {/* 헤더에서는 이름만 간단히 표시하거나, 뒤로가기 등을 흉내낼 수 있음. 생략 또는 단순화 */}
        <span className="font-semibold text-gray-800">{customerName}</span>
      </div>

      {/* 메시지 영역 */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[300px]"
      >
        {/* 기존 메시지 */}
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} customerName={customerName} />
        ))}

        {/* 정산서 미리보기 (아직 전송 안됨) - showPreview가 true일 때만 표시 */}
        {showPreview && previewMessage && !isPasted && (
          <div className="flex justify-end items-end gap-1">
            <span className="text-[10px] text-gray-600 opacity-70">(미리보기)</span>
            <div className="max-w-[75%] bg-[#FEE500] rounded-lg px-3 py-2 shadow-sm">
              <pre className="whitespace-pre-wrap text-xs sm:text-sm text-gray-800 font-sans leading-relaxed">
                {previewMessage}
              </pre>
            </div>
          </div>
        )}

        {/* 전송 완료 축하 메시지 */}
        {isPasted && (
          <div className="flex justify-center py-4">
            <div className="bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 shadow-md">
              <span className="text-xs sm:text-sm text-gray-700 font-medium">
                이렇게 손님에게 정산서를 보내요!
              </span>
            </div>
          </div>
        )}
      </div>

      {/* 입력 영역 */}
      <div className="bg-white border-t border-gray-200 px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-gray-100 rounded-full px-4 py-2 text-sm text-gray-400">
            메시지 입력
          </div>
          {showPasteHint && !isPasted ? (
            <button
              onClick={onPaste}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium transition-all",
                "bg-[#FEE500] text-gray-800 hover:bg-[#FDD835]",
                "animate-pulse shadow-md"
              )}
            >
              붙여넣기
            </button>
          ) : (
            <button
              disabled
              className="px-4 py-2 rounded-full text-sm font-medium bg-gray-200 text-gray-400"
            >
              전송
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// 메시지 말풍선 컴포넌트
function MessageBubble({ message, customerName }: { message: ChatMessage; customerName: string }) {
  const isReceived = message.type === "received";

  if (isReceived) {
    return (
      <div className="flex items-start gap-2">
        <div className="w-10 h-10 rounded-[14px] bg-gray-300 overflow-hidden flex-shrink-0">
          {/* 프로필 이미지 (임시로 아이콘이나 색상 박스) */}
          <img src="/assets/profile-placeholder.png" alt="Profile" className="w-full h-full object-cover opacity-70" onError={(e) => e.currentTarget.style.display = 'none'} />
        </div>
        <div className="flex flex-col gap-1 items-start max-w-[70%]">
          <span className="text-xs text-gray-600 ml-1">{customerName}</span>
          <div className="flex items-end gap-1">
            <div className="bg-white text-gray-800 rounded-lg px-3 py-2 shadow-sm relative">
              {/* 꼬리 효과 (선택 사항) */}
              <div className="absolute top-3 -left-1.5 w-3 h-3 bg-white rotate-45" />
              <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed relative z-10">
                {message.content}
              </pre>
            </div>
            <span className="text-[10px] text-gray-600 mb-0.5 whitespace-nowrap">{message.time}</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-end justify-end gap-1">
      {/* 보낸 메시지: 시간이 왼쪽 */}
      <span className="text-[10px] text-gray-600 mb-0.5 whitespace-nowrap">{message.time}</span>

      {/* 말풍선 */}
      <div
        className={cn(
          "max-w-[75%] rounded-lg px-3 py-2 shadow-sm bg-[#FEE500] text-gray-800",
          message.isPreview && "opacity-70"
        )}
      >
        <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">
          {message.content}
        </pre>
      </div>

      {/* 전송 완료 체크 */}
      {!message.isPreview && (
        <span className="text-[10px] text-gray-500 mb-1">✓</span>
      )}
    </div>
  );
}

export type { ChatMessage };
